import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Stethoscope,
  Car,
  Building2,
  Briefcase,
  Scale,
  ShieldCheck,
  Globe2,
  TrendingUp,
  Users,
  Sparkles,
  Quote,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { DIVISIONS } from "@/data/divisions";
import { api } from "@/lib/api";
import { useCms } from "@/lib/cms";

const iconMap = {
  Stethoscope,
  Car,
  Building2,
  Briefcase,
  Scale,
};

const Hero = () => {
  const navigate = useNavigate();
  const cms = useCms();
  const hero = cms.hero || {};
  const stats = (hero.stats && hero.stats.length > 0) ? hero.stats : [
    { k: "Divisions", v: "5" },
    { k: "Country", v: "1" },
    { k: "Team", v: "80+" },
  ];
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-32 pb-20 md:pt-40 md:pb-28 px-6 md:px-12 lg:px-24 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 grain" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <p className="uppercase text-xs tracking-[0.25em] font-semibold text-[#0284C7] mb-6">
            <Sparkles size={14} strokeWidth={1.5} className="inline mr-2 -mt-0.5" />
            {hero.eyebrow || "A diversified Indo-Pacific conglomerate"}
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light text-slate-900 leading-[1.02]">
            {hero.title_top || "Connecting Industries."}
            <br />
            <span className="text-[#0A2540] font-medium">{hero.title_bottom || "Building Futures."}</span>
          </h1>
          <p className="mt-7 text-base md:text-lg leading-relaxed text-slate-600 max-w-xl">
            {hero.description || "Indo-Pacific Group is a multi-sector enterprise operating across Healthcare, Automobile, Real Estate, IPG Sales and Legal Advisory — built on trust, scale, and a commitment to long-term value."}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Button
              data-testid="hero-cta-explore"
              onClick={() => {
                document.querySelector("#divisions")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="rounded-full bg-[#0A2540] hover:bg-[#0F172A] text-white h-14 px-7 text-base"
            >
              Explore Divisions
              <ArrowUpRight size={18} strokeWidth={1.5} className="ml-1" />
            </Button>
            <Button
              data-testid="hero-cta-book"
              variant="outline"
              onClick={() => navigate("/book")}
              className="rounded-full h-14 px-7 text-base border-slate-300 text-slate-800 hover:bg-slate-50"
            >
              Book Appointment
            </Button>
          </div>

          <div className="mt-14 grid grid-cols-3 max-w-md gap-6">
            {stats.map((s) => (
              <div key={s.k}>
                <div className="font-heading text-3xl font-medium text-slate-900">{s.v}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mt-1">{s.k}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 fade-up" style={{ animationDelay: "200ms" }}>
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-tr from-[#0A2540]/10 to-[#0284C7]/10 rounded-[2rem] -z-10 blur-2xl" />
            <img
              src="https://images.pexels.com/photos/5230087/pexels-photo-5230087.jpeg"
              alt="Indo-Pacific Group HQ"
              className="rounded-3xl w-full h-[520px] object-cover shadow-[0_30px_80px_rgba(10,37,64,0.18)]"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const About = () => {
  const cms = useCms();
  const about = cms.about || {};
  return (
  <section
    id="about"
    data-testid="about-section"
    className="px-6 md:px-12 lg:px-24 py-24 md:py-32"
  >
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500">
          {about.eyebrow || "About the Group"}
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-slate-900 mt-4 leading-[1.1]">
          {about.title || "A trusted multi-sector enterprise."}
        </h2>
      </div>
      <div className="lg:col-span-7 space-y-6 text-slate-600 leading-relaxed">
        <p>{about.paragraph1 || "Founded with a singular vision — to build enduring businesses that serve people, communities and economies across the Indo-Pacific. Today, we operate across five core sectors with operations focused in India."}</p>
        <p>{about.paragraph2 || "Our approach blends operational excellence with disciplined capital allocation. Each division is led by domain experts and supported by group-wide expertise in technology, finance and governance."}</p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 pt-6">
          {[
            { i: ShieldCheck, k: "Governance" },
            { i: Globe2, k: "Global Reach" },
            { i: Users, k: "80+ Team" },
          ].map(({ i: Icon, k }) => (
            <div key={k} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-[#0A2540]">
                <Icon size={18} strokeWidth={1.5} />
              </div>
              <span className="text-sm font-medium text-slate-800">{k}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
  );
};

const DivisionsBento = () => (
  <section
    id="divisions"
    data-testid="divisions-section"
    className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#F8FAFC]"
  >
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
      <div>
        <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500">
          Our Divisions
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-slate-900 mt-4 leading-[1.1] max-w-2xl">
          Five sectors. One commitment to excellence.
        </h2>
      </div>
      <p className="text-slate-600 max-w-md">
        Explore each division, the services we offer, and how we build long-term
        value for customers and communities.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      {DIVISIONS.map((d, i) => {
        const Icon = iconMap[d.iconName];
        // featured layout: first card spans 6, rest 3 each on lg → 2x layout
        const span =
          i === 0
            ? "md:col-span-12 lg:col-span-7 lg:row-span-2"
            : i === 1
            ? "md:col-span-6 lg:col-span-5"
            : "md:col-span-6 lg:col-span-4";
        const tall = i === 0 ? "min-h-[460px]" : "min-h-[240px]";
        return (
          <Link
            key={d.slug}
            data-testid={`division-card-${d.slug}`}
            to={`/divisions/${d.slug}`}
            className={`group relative ${span} ${tall} card-lift overflow-hidden rounded-3xl bg-white border border-slate-100 shadow-[0_8px_30px_rgba(10,37,64,0.04)] flex flex-col`}
          >
            <div className="relative h-1/2 lg:h-3/5 overflow-hidden">
              <img
                src={d.image}
                alt={d.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              <div className="absolute top-5 left-5 flex items-center gap-2 backdrop-blur-md bg-white/70 border border-white/60 rounded-full px-3 py-1.5">
                {Icon && <Icon size={14} strokeWidth={1.5} className="text-[#0A2540]" />}
                <span className="text-xs font-semibold text-slate-800 uppercase tracking-wider">
                  {d.name}
                </span>
              </div>
            </div>
            <div className="p-6 md:p-8 flex-1 flex flex-col">
              <h3 className="font-heading text-xl md:text-2xl font-medium text-slate-900 leading-tight">
                {d.tagline}
              </h3>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed line-clamp-3">
                {d.summary}
              </p>
              <div className="mt-auto pt-6 flex items-center justify-between">
                <span className="text-sm font-medium text-[#0A2540] flex items-center gap-1 group-hover:gap-2 transition-all">
                  Explore
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </span>
                <span className="text-xs text-slate-400">0{i + 1}</span>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  </section>
);

const WhyUs = () => {
  const cms = useCms();
  const why = cms.why_us || {};
  const icons = [ShieldCheck, TrendingUp, Globe2];
  const defaultCards = [
    { title: "Trust & Governance", text: "Backed by best-in-class governance, transparent reporting and decades of operating discipline." },
    { title: "Sustainable Growth", text: "Long-term thinking, prudent capital allocation and resilient business models across cycles." },
    { title: "Indo-Pacific Reach", text: "Headquartered in India with a strong regional footprint and global standards." },
  ];
  const cards = (why.cards && why.cards.length > 0) ? why.cards : defaultCards;
  return (
  <section
    data-testid="why-section"
    className="px-6 md:px-12 lg:px-24 py-24 md:py-32"
  >
    <div className="max-w-3xl">
      <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500">{why.eyebrow || "Why Indo-Pacific"}</p>
      <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-slate-900 mt-4 leading-[1.1]">
        {why.title || "Strength in diversity. Excellence in execution."}
      </h2>
    </div>
    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
      {cards.map((card, i) => {
        const Icon = icons[i % icons.length];
        return (
        <div
          key={card.title || i}
          className="card-lift rounded-3xl border border-slate-100 bg-white p-8 lg:p-10"
        >
          <div className="w-12 h-12 rounded-2xl bg-[#0A2540] text-white flex items-center justify-center">
            <Icon size={20} strokeWidth={1.5} />
          </div>
          <h3 className="font-heading text-xl font-medium text-slate-900 mt-6">{card.title}</h3>
          <p className="text-sm text-slate-600 mt-3 leading-relaxed">{card.text}</p>
        </div>
      );})}
    </div>
  </section>
  );
};

const Testimonials = () => {
  const cms = useCms();
  const t = cms.testimonials || {};
  const defaultItems = [
    { name: "Shekhar Singh", title: "Director Sales", quote: "Indo-Pacific Group consistently demonstrates institutional discipline and long-term vision." },
    { name: "Ronnie Mehra", title: "CMD", quote: "A collaborative partner with deep operational expertise across the Indo-Pacific corridor." },
    { name: "Neeta Singh", title: "Director Health", quote: "Their healthcare network sets a benchmark for clinical excellence and patient care." },
  ];
  const items = (t.items && t.items.length > 0) ? t.items : defaultItems;
  return (
  <section data-testid="testimonials-section" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#F8FAFC]">
    <div className="max-w-2xl">
      <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500">{t.eyebrow || "Voices"}</p>
      <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 mt-4">
        {t.title || "Partnerships built on trust."}
      </h2>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
      {items.map((item, i) => (
        <div key={item.name || i} className="rounded-3xl bg-white border border-slate-100 p-8 shadow-[0_8px_30px_rgba(10,37,64,0.04)]">
          <Quote size={22} strokeWidth={1.5} className="text-[#0284C7]" />
          <p className="text-slate-700 leading-relaxed mt-4">"{item.quote}"</p>
          <div className="mt-6 pt-6 border-t border-slate-100">
            <p className="font-heading font-medium text-slate-900">{item.name}</p>
            <p className="text-sm text-slate-500">{item.title}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
  );
};

const ContactSection = () => {
  const cms = useCms();
  const c = cms.contact || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500">{c.eyebrow || "Contact"}</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-slate-900 mt-4 leading-[1.1]">
            {c.title || "Let's start a conversation."}
          </h2>
          <p className="text-slate-600 leading-relaxed mt-6 max-w-md">
            {c.description || "Reach out for partnerships, investments, careers or enquiries about any of our divisions. Our team responds within one business day."}
          </p>
          <div className="mt-10 space-y-3 text-sm text-slate-700">
            <p><span className="text-slate-500">Email · </span> {c.email || "ronniemehra69@gmail.com"}</p>
            <p><span className="text-slate-500">Phone · </span> {c.phone || "+91 93548 59598"}</p>
            <p><span className="text-slate-500">HQ · </span> {c.address || "D-404 Ansal Tanushree, NH-24 Mehrauli, Ghaziabad, Uttar Pradesh, India — 210002"}</p>
          </div>
        </div>
        <form
          data-testid="contact-form"
          onSubmit={submit}
          className="lg:col-span-7 rounded-3xl border border-slate-100 bg-white p-8 lg:p-12 shadow-[0_8px_30px_rgba(10,37,64,0.04)]"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Name</label>
              <Input
                data-testid="contact-input-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Your full name"
                className="mt-2 h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0A2540]/20"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Email</label>
              <Input
                data-testid="contact-input-email"
                value={form.email}
                onChange={update("email")}
                type="email"
                placeholder="you@company.com"
                className="mt-2 h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0A2540]/20"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Phone</label>
              <Input
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+91 ..."
                className="mt-2 h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0A2540]/20"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Subject</label>
              <Input
                data-testid="contact-input-subject"
                value={form.subject}
                onChange={update("subject")}
                placeholder="How can we help?"
                className="mt-2 h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0A2540]/20"
              />
            </div>
          </div>
          <div className="mt-5">
            <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Message</label>
            <Textarea
              data-testid="contact-input-message"
              value={form.message}
              onChange={update("message")}
              placeholder="Tell us a bit more..."
              className="mt-2 min-h-[140px] rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0A2540]/20"
            />
          </div>
          <Button
            data-testid="contact-form-submit"
            type="submit"
            disabled={loading}
            className="mt-8 rounded-full bg-[#0A2540] hover:bg-[#0F172A] text-white h-12 px-7"
          >
            {loading ? "Sending..." : "Send Message"}
            <ArrowUpRight size={16} strokeWidth={1.5} className="ml-1" />
          </Button>
        </form>
      </div>
    </section>
  );
};

const Home = () => {
  return (
    <div data-testid="home-page" className="bg-white">
      <Hero />
      <About />
      <DivisionsBento />
      <WhyUs />
      <Testimonials />
      <ContactSection />
    </div>
  );
};

export default Home;
